<template>
  <section class="mt-3" id="chart">
    <b-alert variant="warning" :show="incomplete.length > 0">
      Alcuni dati non sono completi:<strong>{{ incomplete.join(', ') }}</strong>
    </b-alert>

    <div v-for="(chart, i) in formattedCharts" :key="i">
      <h4 v-if="chart.title">{{ chart.title }}</h4>
      <div v-if="! chart.showTable">
        <apexchart :height="filter.split ? minMainChartHeight : maxMainChartHeight"
                   :type="type" :options="chart.options"
                   :series="chart.series" />
        <apexchart v-if="showAreaChart"
                   type="area"
                   :height="70"
                   :options="chart.optionsBrush"
                   :series="chart.series" />
      </div>
      <div v-else>
        <div class="table-toolbar d-print-none" style="float:right">
          <a href="#" @click="chart.showTable = false">
            <font-awesome-icon icon="times" />
          </a>
          <a class="table-toolbar-control" @click="chart.toolbar.open = !chart.toolbar.open">
            <font-awesome-icon icon="bars" />
          </a>
          <div v-if="chart.toolbar.open" class="table-toolbar-list">
            <a @click="download('excel', chart)">
              Download Excel
            </a>
            <a @click="download('csv', chart)">
              Download CSV
            </a>
          </div>
        </div>
        <chart-table :filter="filter" :series="tableSeries" :dates="tableDates" :id="chart.id" />
      </div>
    </div>

    <div v-if="isAggregateMode" class="aggregate-selection-block">

      <div v-for="(measure, index) in uniqueMeasures" class="aggregate-selection">
        <div class="aggregate-selection-label">
          {{ measure }}:
        </div>
        <select v-model="measuresAggregateMode[index]" class="aggregate-selection-selector">
          <option v-for="type in availableMeasureTypes" :value="type.value">
            {{ type.label }}
          </option>
        </select>
      </div>

      <b-button v-b-modal.modal-customize>Personalizza</b-button>

      <b-modal id="modal-customize" title="Personalizza">
        <div v-for="(range, index) in allRangesWithMeasure" class="aggregate-selection">
          <div class="aggregate-selection-label">
            {{ range.name }} ({{ range.measure }}):
          </div>
          <select v-model="rangeAggregateModes[index]" class="aggregate-selection-selector">
            <option value="">Nessuna</option>
            <option v-for="type in availableMeasureTypes" :value="type.value">
              {{ type.label }}
            </option>
          </select>
        </div>
      </b-modal>

    </div>
  </section>
</template>

<script>
import ChartTable from '../../Table';
import buttonsDef from './line-buttons';
import { makeid } from '../../helpers';
import moment from 'moment';
import XLSX from 'xlsx';
import NeedUpdateMixin from "../../NeedUpdateMixin";
import {debounce} from "lodash";
import chart from "../../../Dashboard/VectorWidgets/widgets/chart";
import {getSamplings} from "../../../../helpers/measure";

const MONTH_CATEGORIES_FORMAT = 'MMM YY';
const MINUTES_CATEGORIES_FORMAT = 'D MMM H:mm';
const DAY_WEEK_CATEGORIES_FORMAT = 'D MMM';

export default {
  name: 'LineChart',

  components: { ChartTable },

  props: {
    filter: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: 'line',
    },
  },

  mixins: [NeedUpdateMixin],

  data() {
    return {
      dates: [],
      tableSeries: [],
      tooltipData: [],

      previousBarState: null,
      charts: [],
      incomplete: [], // Warning messages
      area: true, // Show area

      minMainChartHeight: 200,
      maxMainChartHeight: 400,

      isAggregateMode: false,
      isRolling: false,
      measuresAggregateMode: ['sum', 'sum', 'sum', 'sum'],
      rangeAggregateModes: [],
      availableMeasureTypes: [
        {
          value: 'sum',
          label: 'Somma',
        },
        {
          value: 'avg',
          label: 'Media',
        },
      ],

      vectorNamesWithMeasures: [],

      colors: this.$store.state.colors,
      yaxisDefault: {
        axisTicks: {
          show: true,
        },
        title: {
          text: "kW",
        },
        tooltip: {
          enabled: false,
        },
        labels: {
          formatter: x => this.f(x),
        },
      },

      options: {
        chart: {
          id: 'line-chart',
          group: 'channels',
          animations: {
            enabled: false,
          },
          locales: this.getChartsLocales(),
          defaultLocale: "it",
          toolbar: {
            tools: {
              download: false,
              selection: true,
              zoom: '<img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7">',
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: '<img title="Reset zoom" src="/static/icons/icon-2.png" width="16">',
              customIcons: [{
                icon: '<img title="Tabella" src="/static/icons/icon-25.png" width="16">',
                class: 'apex-custom-button volta-icon-table',
                title: 'Tabella',
                index: 0,
                click: this.tableButtonClick,
              }],
            },
          },
          zoom: {
            type: 'x',
            enabled: true,
          },
          dataLabels: {
            enabled: false,
          },
          stacked: false,
          stackType: null,
        },
        colors: this.$store.state.colors,
        dataLabels: { enabled: false },
        stroke: {
          width: 2,

          dashArray: this.getOptionsStrokeDashArray(this.type),
        },
        markers: {
          size: 0,
          strokeColor: '#333',
        },
        xaxis: {
          type: 'datetime',
          categories: [],
        },
        tooltip: {
          enabled: true,
          followCursor: false,
          y: {
            formatter: (v, o) => {
              if (this.isPercentStacked) {
                const total = o.series.reduce(
                  (sum, currentRange) => currentRange[o.dataPointIndex] ? sum + currentRange[o.dataPointIndex] : sum,
                  0,
                );
                const percent = (100 * v) / total;
                const percentFormatted = percent.toFixed(2).replace('.', ',');
                return `${percentFormatted}%`;
              }

              if (v) {
                return this.f(v)
              }

              return v
            },
            title: {
              formatter: (seriesName, o) => {
                return seriesName + " " + this.getMu(seriesName)
              },
            },
          },
          x: {
            formatter: this.xTooltipFormatter,
          },
        },
        grid: {
          row: {
            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
            opacity: 0.5,
          },
        },
        yaxis: [
          {
            //seriesName: 'data-0',
            axisBorder: {
              show: true,
            },
            axisTicks: {
              show: true,
            },
            title: {
              text: "kW",
            },
            tooltip: {
              enabled: false,
            },
            labels: {
              formatter: x => this.f(x),
            },
          },
        ],

        legend: {
          itemMargin: {
            horizontal: 10,
            vertical: 5,
          },
        }
      },
      defaultAreaChartOptions: {
        chart: {
          id: 'chartBrush',
          animations: {
            enabled: false,
          },
          brush: {
            target: 'line-chart',
            enabled: true,
          },
          selection: {
            enabled: false,
          },
        },
        grid: {
          show: false,
        },
        legend: {
          show: false,
        },
        xaxis: {
          labels: {
            show: false,
          },
        },
        yaxis: {
          show: false,
          labels: {
            formatter: x => this.f(x),
          },
        },
      },
      series: [],
      mus: [],
      channels: [],
      previousData: [],
      previousTime: {},
      dateFormat: {
        m: "MM/YYYY",
        w: "DD/MM/YYYY",
        d: "DD/MM/YYYY",
        "60": "DD/MM/YYYY",
      },
      currentPeriodDates: [],
      previousPeriodDates: [],
    };
  },

  computed: {
    allRangesWithMeasure() {
      return _.cloneDeep(this.charts.reduce((series, chart) => {
        const ranges = _.cloneDeep(chart.series).map((range) => {
          const measure = this.channels.find((channel) => channel.name === range.name).mu;

          return {
            ...range,
            measure,
          };
        });

        return [...series, ...ranges];
      }, []));
    },

    formattedCharts() {
      if (!this.isAggregateMode) {
        return this.charts;
      }

      const formattedCharts = [];

      this.charts.forEach((chart) => {
        const formattedChart = _.cloneDeep(chart);
        const series = _.cloneDeep(formattedChart.series);

        const groupedSeriesByMeasure = [];

        series.forEach((range) => {
          const measure = this.channels.find((channel) => channel.name === range.name).mu;
          const indexForFindingAggregationMode = this.allRangesWithMeasure.findIndex(
            (rangeWithMeasure) => rangeWithMeasure.name === range.name);
          const aggregateMode = this.rangeAggregateModes[indexForFindingAggregationMode];
          const index = groupedSeriesByMeasure.findIndex(
            (group) => group.measure === measure && group.aggregateMode === aggregateMode,
          );

          if (index === -1 || !aggregateMode) {
            groupedSeriesByMeasure.push({
              aggregateMode: aggregateMode,
              measure: measure,
              series: [range],
            })
          }
          else {
            groupedSeriesByMeasure[index].series.push(range);
          }
        });

        formattedChart.series = [];
        groupedSeriesByMeasure.forEach((group) => {
          const measureType = this.availableMeasureTypes.find((type) => type.value === group.aggregateMode);
          const formattedRange = {
            name: measureType ? `${measureType.label}: (` : '',
            data: [],
          };

          group.series.forEach((range) => {
            formattedRange.name = `${formattedRange.name} ${range.name},`;
            formattedRange.data = range.data.map((value, index) => {
              const formattedValue = value || 0;

              return formattedRange.data[index] ? formattedRange.data[index] + formattedValue : formattedValue;
            });
          });

          if (group.aggregateMode === 'avg') {
            formattedRange.data = formattedRange.data.map((value) => value / group.series.length);
          }

          // Remove last comma and add right parenthesis
          formattedRange.name = formattedRange.name.slice(0, -1);
          if (measureType) {
            formattedRange.name = `${formattedRange.name} )`;
          }

          formattedChart.series.push(formattedRange);
        });

        const firstYAxis = _.cloneDeep(formattedChart.options.yaxis[0]);

        const yAxises = [];
        formattedChart.series.forEach((range, index) => {
          const group = groupedSeriesByMeasure[index];

          const previousRanges = formattedChart.series.slice(0, index);
          const rangeWithSameMeasure = previousRanges.find((previousRange, previousRangeIndex) => {
            const previousGroup = groupedSeriesByMeasure[previousRangeIndex];
            return previousGroup.measure === group.measure;
          });

          if (rangeWithSameMeasure) {
            yAxises.push({
              seriesName: rangeWithSameMeasure.name,
              show: false,
            });
            return;
          }

          const yAxis = _.cloneDeep(firstYAxis);
          const vectorWithNameAndMeasure = this.vectorNamesWithMeasures.find((vector) => vector.measure === group.measure);
          yAxis.title.text = `${vectorWithNameAndMeasure.name} (${vectorWithNameAndMeasure.measure})`;
          yAxis.seriesName = range.name;
          if (index) {
            yAxis.opposite = true;
          }

          yAxises.push(yAxis);
        });
        formattedChart.options.yaxis = yAxises;

        formattedCharts.push(formattedChart);
      });

      return formattedCharts;
    },

    uniqueMeasures() {
      return [...new Set(this.channels.map(item => item.mu))];
    },

    showAreaChart() {
      return this.filter.mode === 'MEASURES' && this.area;
    },

    isIndicators() {
      return this.filter.mode === 'INDICATORS';
    },

    isComparing() {
      return this.filter.compare !== 'no';
    },

    isBar() {
      return this.type === 'bar';
    },

    isStacked() {
      return this.options && this.options.chart && this.options.chart.stacked === true;
    },

    isPercentStacked() {
      return this.isStacked && this.options.chart.stackType === '100%';
    },

    xAxisFormat() {
      let format = null;

      if (this.filter.sampling === '1' || this.filter.sampling === '15' || this.filter.sampling === '60') {
        format = MINUTES_CATEGORIES_FORMAT;
      }
      else if (this.filter.sampling === 'd' || this.filter.sampling === 'w') {
        format = DAY_WEEK_CATEGORIES_FORMAT;
      }
      else {
        format = MONTH_CATEGORIES_FORMAT;
      }

      return format;
    },

    tableDates() {
      return {
        previousPeriodDates: this.previousPeriodDates,
        currentPeriodDates: this.currentPeriodDates,
      };
    },
  },

  watch: {
    measuresAggregateMode() {
      this.initAggregationModeForRanges();
    },

    filter(newFilters, oldFilters) {
      if (newFilters.compare !== oldFilters.compare) {
        this.options.stroke = {
          ...this.options.stroke,

          dashArray: this.getOptionsStrokeDashArray(this.type),
        };
      }

      debounce(this.reload, 10)();
    },

    type(newType, oldType) {
      const newOptions = {
        ...this.options,
        stroke: {
          ...this.options.stroke,

          dashArray: this.getOptionsStrokeDashArray(newType),
        },
      };

      if (oldType === 'bar' && newType !== 'bar') {
        this.previousBarState = {
          stacked: this.options.chart.stacked,
          stackType: this.options.chart.stackType,
        };
      }

      if (newType === 'bar' && this.previousBarState) {
        newOptions.chart = {
          ...newOptions.chart,
          ...this.previousBarState,
        };
      }

      if (newType !== 'bar') {
        this.options.chart.stacked = false;
        this.options.chart.stackType = null;
      }

      this.options = newOptions;
    },
  },

  created() {
    this.reload();
  },

  methods: {
    initAggregationModeForRanges() {
      const newRangeAggregateModes = [];
      // Set default aggregate mode for each range
      this.allRangesWithMeasure.forEach((rangeWithMeasure, index) => {
        const measureIndex = this.uniqueMeasures.findIndex((uniqueMeasure) => uniqueMeasure === rangeWithMeasure.measure);
        const aggregateMode = this.measuresAggregateMode[measureIndex];

        newRangeAggregateModes[index] = aggregateMode;
      });
      this.rangeAggregateModes = newRangeAggregateModes;
    },

    xTooltipFormatter(index) {
      let categoryDate = null;
      if (Array.isArray(index)) {
        categoryDate = index[0];
      }
      else if (Number.isInteger(index)) {
        categoryDate = this.currentPeriodDates[index - 1];
      }

      if (!categoryDate) {
        return '';
      }

      let m = moment(categoryDate);

      let { xAxisFormat } = this;

      if (this.filter.sampling === '1' || this.filter.sampling === '15' || this.filter.sampling === '60' || this.filter.sampling === 'd') {
        xAxisFormat = `${xAxisFormat} - ddd`;
      }

      let ext = '';
      if (this.isComparing) {
        const categoryIndex = this.currentPeriodDates.findIndex(
          (category) => category == categoryDate,
        );

        const previousDataValue = this.previousPeriodDates[categoryIndex];

        if (previousDataValue) {
          let dateString = previousDataValue;
          const m2 = moment(dateString);
          const formattedDate = m2.format(xAxisFormat);
          ext = `<span style='border-bottom:1px dashed #888'>${formattedDate}</span> - `;
        }
      }

      return  ext + m.format(xAxisFormat);
    },

    tableButtonClick(a, b, c) {
      let arr = []
      this.charts.forEach((c, i) => {
        if (c.id == b.config.id || this.charts.length == 1) {
          c.showTable = true
          c.series.forEach(series => {
            arr = series.data.map(i => {
              i = i ? +i.toFixed(2) : null
              return i
            })
            this.tableSeries.push({
              data: arr,
              name: series.name
            })
          })
        }
      })
    },

    async reload() {
      this.$emit('aggregate-mode-changed', this.isAggregateMode);

      if (this.loading) {
        return;
      }

      this.charts = [];
      this.channels = [];

      this.$store.commit('loading');

      const data = await this.getChartData();

      if (!data) {
        return;
      }

      if (Array.isArray(data.mus)) {
        this.vectorNamesWithMeasures = data.mus.map((mu, index) => {
          return {
            measure: mu,
            name: data.vectors ? data.vectors[index] : '',
          }
        })
      }

      this.incomplete = data.incomplete;
      this.mus = data.mus;

      let series = this.getSeriesFromServerData(data);

      const options = this.getOptionsFromServerData(data, series);
      const defaultAreaChart = this.generateDefaultAreaChartOptions(options);

      this.tooltipData = await this.formatTooltipSeries(series)
      if (this.isStacked && options.countDifferentYAxis > 1) {
        this.options.chart.stacked = false;
        this.options.chart.stackType = null;

        this.reload();

        return;
      }

      if (this.filter.split) {
        this.charts = [];
        data.columns.forEach((c, i) => {
          if (i == 0) {
            return;
          }

          let mu = data.mus[i - 1]
          let yaxis = _.cloneDeep(this.yaxisDefault)

          yaxis.show = true
          yaxis.axisBorder = { show: true }
          yaxis.title = {}
          yaxis.title.text = data.vectors[i - 1] + " [" + mu + "]"
          yaxis.opposite = false
          options.yaxis = yaxis

          let seriesOptions = _.cloneDeep(options)
          seriesOptions.colors = [this.colors[i - 1 % this.colors.length]]

          const optionsBrush = _.cloneDeep(defaultAreaChart)
          optionsBrush.colors = [this.colors[i - 1 % this.colors.length]]
          optionsBrush.legend = { show: false }

          optionsBrush.yaxis = {
            //tickAmount: 1,
            title: {
              text: ".",
              style: {
                color: "#fff",
              },
            },
            labels: {
              style: {
                colors: ["#fff"],
                cssClass: 'apexcharts-yaxis-label-brush',
              },
              formatter: x => this.f(x),
            },
          }

          this.charts.push({
            options: seriesOptions,
            optionsBrush,
            series: [series[i - 1]],
            title: c,
            toolbar: { open: false },
            showTable: false,
            id: makeid(),
          })
        })

        // this.$toasted.show('Completato', { duration: 1000, icon: 'check', theme: "bubble" })
        this.$notify({
          type: "success",
          text: "Completato",
          title: 'Successo'
        }); 
      }
      else {
        this.charts = [{
          options,
          optionsBrush: defaultAreaChart,
          series,
          id: makeid(),
          toolbar: { open: false },
          showTable: false,
        }];
      }

      this.$store.commit("stopLoading");

      if (this.isAggregateMode) {
        this.initAggregationModeForRanges();
      }
    },

    async getChartData() {
      const params = _.cloneDeep(this.filter);

      if (this.isRolling) {
        params.rolling = true;
      }

      if (this.isIndicators) {
        params.indicators = [...params.channels];
        delete params.channels;
      }

      const response = await this.$store.dispatch('charts/get', params).catch(() => {
        this.$store.commit("stopLoading");
      });

      if (!response) {
        return;
      }

      let data = response;

      if (this.isComparing) {
        data = response[0];
        this.previousData = response[1];
        this.previousPeriodDates = this.previousData.values.map((value) => value[0]);
      }

      this.currentPeriodDates = data.values.map((value) => value[0]);

      return data;
    },

    getSeriesFromServerData(data) {
      const series = [];

      // Get max value for each measuring unit
      const max = {}
      _.uniq(data.mus).forEach(mu => {
        max[mu] = -100000000
      })

      data.columns.forEach((columnName, index) => {
        if (index === 0) {
          return
        }

        let mu = data.mus[index - 1]

        this.channels.push({
          name: columnName,
          mu,
        })

        const range = {
          name: columnName,
          data: [],
        }

        data.values.forEach((value) => {
          // show also negative numbers
          let rangeValue = value[index]; //value[index] === 0 || value[index] > 0 ? value[index] : null;
          max[mu] = Math.max(rangeValue, max[mu]);

          range.data.push(rangeValue);
        });

        series.push(range);

        if (this.isComparing) {
          const compareRange = {
            name: columnName + "*",
            data: this.previousData.values.map(
              (v, j) => v[index] == 0 || v[index] > 0 ? v[index] : 0,
            ),
          }

          series.push(compareRange);
        }
      })
      return series;
    },

    getOptionsFromServerData(data, series) {
      let options = _.cloneDeep(this.options);

      if (this.isComparing) {
        options.tooltip.y = []

        //Formatter for main range
        data.columns.forEach((unusedValue, index) => {
          if (index === 0) {
            return;
          }

          options.tooltip.y.push({
            formatter: (v, o) => {
              let previous = this.previousData.values[o.dataPointIndex][index]
              if (!previous) {
                return v ? this.f(v) : v
              }

              //let diff = v < previous ? ((v - previous) / v * 100)
              //                    : ((previous - v) / previous * 100) * -1
              let diff = (v - previous) / previous * 100
              let col = "#090"

              if (!Number.isFinite(diff)) {
                return this.f(v);
              }

              if (diff > 0) {
                diff = "⬆" + diff.toFixed(2);
                col = "#f00"
              }
              else {
                diff = "⬇" + diff.toFixed(2);
              }

              return this.f(v) + " <span style='color:" + col + "'>" + diff + "%</span>"
            },
            title: {
              formatter: (seriesName, o) => {
                return seriesName + " " + this.getMu(seriesName)
              },
            },
          })

          //Formatter for compared range
          options.tooltip.y.push({
            formatter: (v, o) => {
              if (v) {
                return this.f(v)
              }

              return v
            },
            title: {
              formatter: (seriesName, o) => {
                return "<span style='color:#888;border-bottom:1px dashed #888'>" + seriesName + " " + " " + this.getMu(seriesName) + "</span>"
              },
            },
          })
        })
      }

      options.yaxis = [];
      options.xaxis.type = 'category';
      options.xaxis.categories = [...data.values.map(v => [v[0]])];


      options.xaxis.labels = {
        formatter: (v) => {
          if (!v) {
            return null;
          }

          return moment.utc(v[0]).format(this.xAxisFormat);
        },
      }

      if (!this.filter.split) {
        let opposite = true;
        const cols = [];
        const mus = [];
        const vectors = [];

        for (let i = 1; i < data.columns.length; i++) {
          cols.push(data.columns[i]);
          mus.push(data.mus[i - 1]);
          vectors.push(data.vectors[i - 1]);

          if (this.isComparing) {
            cols.push(data.columns[i] + "*");
            mus.push(data.mus[i - 1]);
            vectors.push(data.vectors[i - 1]);
          }
        }

        cols.forEach((c, i) => {
          let yaxis = _.cloneDeep(this.yaxisDefault);
          let mu = mus[i];
          yaxis.seriesName = series[i].name;

          yaxis.show = true;
          yaxis.axisBorder = { show: true };

          if (this.filter.costs) {
            yaxis.title.text = "Costo (€)"
          }
          else {
            yaxis.title.text = vectors[i] + " [" + mu + "]"
          }

          yaxis.opposite = yaxis.show ? !opposite : false;

          const yAxisWithSameName = options.yaxis.find(
            (axis) => axis.title && axis.title.text === yaxis.title.text,
          );

          if (yAxisWithSameName) {
            options.yaxis.push({
              seriesName: yAxisWithSameName.seriesName,
              show: false,
            });
          }
          else {
            if (options.yaxis.length > 0) {
              yaxis.opposite = true;
            }

            options.yaxis.push(yaxis);
          }

          opposite = yaxis.show ? !opposite : opposite;
        });

        options.countDifferentYAxis = options.yaxis.filter((yaxis) => yaxis.show).length;

        if (this.isPercentStacked) {
          options.yaxis = options.yaxis[0];
          options.yaxis.title.text = 'Percentuale (%)';
        }
      } else {
        options.yaxis = _.cloneDeep(this.yaxisDefault);

        if (this.filter.costs) {
          options.yaxis.title.text = "Costo (€)"
        }

        options.countDifferentYAxis = 1;
      }

      // Set custom buttons to the toolbar
      options.chart.toolbar.tools.customIcons = [
        ...options.chart.toolbar.tools.customIcons,
        ...this.getButtons(series, options.countDifferentYAxis),
      ];

      return options;
    },

    generateDefaultAreaChartOptions(options) {
      const areaChartOptions = _.cloneDeep(this.defaultAreaChartOptions);
      areaChartOptions.colors = _.cloneDeep(this.colors);

      areaChartOptions.yaxis = {};
      areaChartOptions.yaxis.tickAmount = 1;
      areaChartOptions.yaxis.title = {
        text: " ",
        style: {
          colors: ["#fff"],
        },
      };
      areaChartOptions.yaxis.labels = {
        style: {
          cssClass: 'apexcharts-yaxis-label-brush',
        },
        formatter: x => this.f(x),
      };

      return areaChartOptions;
    },

    getButtons(series, countDifferentYAxis) {
      let buttons = [];

      if (this.isIndicators && this.filter.sampling === 'm') {
        buttons.push(buttonsDef.rollingButton);
      }

      if (series.length > 1 && !this.filter.split && this.filter.compare === 'no') {
        buttons.push(buttonsDef.splitButton);
        buttons.push(buttonsDef.aggregateButton);
      }

      if (this.filter.split) {
        buttons.push(buttonsDef.mergeButton);
      }

      if (series.length > 1 && this.type === 'bar' && countDifferentYAxis === 1) {
        buttons.push(buttonsDef.unStackedButton);
        buttons.push(buttonsDef.stackedButton);
        buttons.push(buttonsDef.stackedPercentButton);
      }

      if (this.filter.mode === 'MEASURES') {
        buttons.push(buttonsDef.measuresButton);
        buttons.push(buttonsDef.eurButton);
      }

      // Initialize buttons with current component
      return buttons.map(button => (button(this)));
    },

    getDates() {
      let dates = [];
      this.currentPeriodDates.forEach(date => {
        dates.push(date.split(' '));
      });
      return dates
    },
    async download(type, chart) {
      chart.toolbar.open = false
      if (type === 'excel') {
        const dates = await this.getDates()

        let aoa = dates[0].length === 1 ? [['Data'],['Totale']] : [['Data', 'Ora'],['Totale', 'Totale']]
          let arr = [];

          chart.series.forEach(item => {
            aoa[0].push(item.name)
            aoa[1].push(item.data.reduce((a, b) => a + b, 0))
            arr.push(item.data)
          })
          for(let i = 0; i < chart.series[0].data.length; i++) {
            let a = []
            arr.forEach(item => {
              item[i] ? a.push(item[i]).toString().split(', ') : a.push(null)
            })
            if (dates[0].length === 1) {
              aoa.push([moment(dates[i][0]).format('l'), ...a])
            } else {
              aoa.push([moment(dates[i][0]).format('l'), moment(dates[i][1], 'hh:mm:ss').format('hh:mm'), ...a])

            }
          }
          const ws = XLSX.utils.aoa_to_sheet(aoa);
          const wb = XLSX.utils.book_new();

          XLSX.utils.book_append_sheet(wb, ws, 'Sheet');

          XLSX.writeFile(wb, "export.xlsx");

      }
      if (type == 'csv') {
        let rows = []
        if (chart.series.length > 0) {

          let header = ["Data"]

          if (this.filter.sampling == "60") {
            header.push("Ora")
          }

          chart.series.forEach(s => {
            header.push(s.name)
          })

          rows.push(header.join(","))

          chart.series[0].data.forEach(d => {

            var item = []
            item.push(moment(d[0]).format(this.dateFormat[this.filter.sampling]))

            if (this.filter.sampling == "60") {
              item.push(moment(d[0]).format("hh:mm"))
            }

            chart.series.forEach(s => {

              var value = _.filter(s.data, _d => _d[0] == d[0])
              if (value) {
                item.push(value[0][1])
              }
              else {
                item.push("")
              }
            })

            rows.push(item.join(","))
          })

          var downloadLink = document.createElement('a');
          downloadLink.href = "data:text/csv;charset=utf-8," + encodeURI(rows.join("\n"));
          downloadLink.download = "export-" + this.filter.sampling + ".csv";
          document.body.appendChild(downloadLink);
          downloadLink.innerHTML = "DOWNLOAD"
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      }
    },

    getOptionsStrokeDashArray(type) {
      return type === 'line' && this.isComparing
             ? (new Array(22)).fill(0).map((v, index) => (index % 2 ? 5 : 0))
             : 0;
    },

    f(v) {
      return v ? v.toFixed(2) : 0;
      //return v ? this.$options.filters.formatDecimal(v) : 0;
    },

    getMu(rangeName) {
      let name = _.endsWith(rangeName, "*") ? rangeName.replace(/\*/, '') : rangeName;

      const channel = this.channels.find(c => c.name == name);
      return channel ? `[${channel.mu}]` : '';
    },

    formatTooltipSeries(data) {
      let datas = data.map(item => {
        let diff = Math.max(...item.data) - Math.min(...item.data)
        let arr = [];

        if(diff >= 100) {
          item.data.map(i => {
           i ? arr.push(Math.round(i)) : null
          })
        } else if (diff > 10 && diff <= 100) {
          item.data.map(i => arr.push(i ? i.toFixed(1) : null))
        } else if (diff > 0 && diff <= 10) {
          item.data.map(i => {
            if (i < 0.01 && i) {
              arr.push(i)
            } else {
              arr.push(i ? i.toFixed(2) : null)
            }
          })
        }
        return arr
      })
    }
  },
}
</script>

<style>
.chart-button {
  font-size: 11px;
  position: relative;
  top: 5px;
  cursor: pointer;
}

.apexcharts-xaxistooltip-text {
  min-width: 100px !important;
  text-transform: capitalize;
}

.apexcharts-toolbar {
  max-width: 100em;
  right: 36px;
}

.apexcharts-toolbar .apex-custom-button {
  height: 20px;
  line-height: 24px;
  position: relative;
  margin-right: 3px;
  cursor: pointer;
}

.apexcharts-toolbar .apex-custom-button.selected {
  color: #008FFB;
}

.apexcharts-zoom-icon,
.apexcharts-reset-icon {
  margin-left: 0;
  position: relative;
  top: 1px;
}

.apexcharts-yaxis-label-brush {
  opacity: 0;
}

.apexcharts-tooltip {
  text-transform: capitalize;
}

.icon-network {
  top: 3px;
}

.aggregate-selection-block {
  margin-left: 70px;
}

.aggregate-selection {
  display: flex;
  margin-bottom: 10px;
}

.aggregate-selection:last-child {
  margin-bottom: 0;
}

.aggregate-selection-label {
  min-width: 70px;
  padding-right: 25px;
}
</style>



// WEBPACK FOOTER //
// src/components/Charts/Types/Apex/Line.vue